import { TeamMemberRole } from './translation_namespace/professional_namespace';
import { Scope } from './scopes';

export const ROLE_SCOPES: Record<TeamMemberRole, Scope[]> = {
  [TeamMemberRole.OWNER]: ['*'],

  [TeamMemberRole.MANAGER]: [
    // Agenda — tout le monde
    'agenda',
    'agenda.appointment.read_all',
    'agenda.appointment.create_all',
    'agenda.appointment.update_all',
    'agenda.appointment.delete_all',
    'agenda.availability.read_all',
    'agenda.availability.create_all',
    'agenda.availability.update_all',
    'agenda.availability.delete_all',
    'agenda.holiday.read_all',
    'agenda.holiday.create_all',
    'agenda.holiday.update_all',
    'agenda.holiday.delete_all',
    'agenda.sync.read',
    'agenda.sync.update',
    // Paramètres de réservation (champs personnalisés)
    'agenda.booking_params',
    // Catalogue — CRUD complet
    'catalog',
    // Clients
    'customer',
    // Campagnes
    'campaign',
    // Ventes
    'sales',
    // Site
    'website',
    // Établissement — tout SAUF abonnement et config Stripe
    'establishment.info',
    'establishment.place',
    'establishment.team',
    'establishment.resource',
    'establishment.google_sync',
    'establishment.tag',
    'establishment.email_pro',
    'establishment.seo_content',
    'establishment.stripe_connect.read',
    // Domaines autorisés pour les widgets embarqués (Apple Pay Stripe)
    'establishment.widget_domain',
    // API & webhooks (Max only — gated par le plan)
    'api',
    'webhook',
    // Statistiques — page et widgets d'accueil (OWNER et MANAGER seulement) ; les paliers
    // Pro / Max sont tranchés par l'intersection avec la formule (FEATURES.ADVANCED_STATS /
    // TEAM_STATS), jamais par le rôle.
    'stats.read',
    'stats_advanced.read',
    'stats_team.read',
    // Notifications — feed + gestion des preferences
    'notification',
  ],

  [TeamMemberRole.RECEPTIONIST]: [
    // Agenda — voit et gère les RDV de tout le monde
    'agenda.appointment.read_all',
    'agenda.appointment.create_all',
    'agenda.appointment.update_all',
    // Dispos/congés — consultation de toute l'équipe
    'agenda.availability.read_all',
    'agenda.holiday.read_all',
    'agenda.sync.read',
    // Sa propre synchro d'agenda externe (Google/Outlook/iCal) ; `update_all` reste aux managers.
    'agenda.sync.update',
    // Clients (accueil = point de contact)
    'customer',
    // Ventes — encaissement
    'sales.payment.read',
    'sales.payment.create',
    'sales.payment.update',
    'sales.voucher.read',
    'sales.voucher.create',
    // Encaisser un bon / une carte cadeau au comptoir (+ renvoi de l'email cadeau), décision du 2026-10-10.
    'sales.voucher.update',
    'sales.promo_code.read',
    // Catalogue — lecture (pour renseigner les clients)
    'catalog.prestation.read',
    'catalog.supplement.read',
    'catalog.category.read',
    'catalog.pack.read',
    // Notifications — feed + mark as read
    'notification.read',
    'notification.update',
  ],

  [TeamMemberRole.EMPLOYEE]: [
    // Agenda — ses propres données uniquement
    'agenda.appointment.read',
    'agenda.appointment.create',
    'agenda.appointment.update',
    'agenda.appointment.delete',
    'agenda.availability.read',
    'agenda.availability.create',
    'agenda.availability.update',
    'agenda.availability.delete',
    'agenda.holiday.read',
    'agenda.holiday.update',
    'agenda.sync.read',
    // Sa propre synchro d'agenda externe (Google/Outlook/iCal) ; `update_all` reste aux managers.
    'agenda.sync.update',
    // Catalogue — lecture
    'catalog.prestation.read',
    'catalog.supplement.read',
    'catalog.category.read',
    'catalog.pack.read',
    // Clients
    'customer',
    // Campagnes
    'campaign',
    // Ventes — lecture
    'sales.payment.read',
    'sales.voucher.read',
    'sales.promo_code.read',
    // Site — actualités
    'website.news',
    // Etablissement — lecture
    'establishment.info.read',
    'establishment.place.read',
    'establishment.team.read',
    'establishment.tag.read',
    // Paramètres de réservation — lecture seule
    'agenda.booking_params.read',
    // Notifications — feed + mark as read
    'notification.read',
    'notification.update',
  ],

  [TeamMemberRole.APPRENTICE]: [
    // Agenda — ses propres données, pas de suppression
    'agenda.appointment.read',
    'agenda.appointment.create',
    'agenda.appointment.update',
    'agenda.availability.read',
    'agenda.holiday.read',
    'agenda.sync.read',
    // Sa propre synchro d'agenda externe (Google/Outlook/iCal) ; `update_all` reste aux managers.
    'agenda.sync.update',
    // Catalogue — lecture
    'catalog.prestation.read',
    'catalog.supplement.read',
    'catalog.category.read',
    // Clients — lecture
    'customer.read',
    // Etablissement — lecture
    'establishment.info.read',
    'establishment.place.read',
    'establishment.team.read',
    // Notifications — feed + mark as read
    'notification.read',
    'notification.update',
  ],

  [TeamMemberRole.VIEWER]: [
    // Lecture seule sur tout (avec _all pour le reporting agenda)
    'agenda.appointment.read_all',
    'agenda.availability.read_all',
    'agenda.sync.read',
    'catalog.prestation.read',
    'catalog.supplement.read',
    'catalog.category.read',
    'catalog.pack.read',
    'customer.read',
    'campaign.email.read',
    'campaign.sms.read',
    'sales.payment.read',
    'sales.voucher.read',
    'sales.promo_code.read',
    'website.stats.read',
    'website.builder.read',
    'website.news.read',
    'establishment.info.read',
    'establishment.place.read',
    'establishment.team.read',
    'establishment.subscription.read',
    'establishment.tag.read',
    // Notifications — lecture seule
    'notification.read',
  ],
};

/**
 * Échelle des rôles d'équipe. `ROLE_SCOPES` dit ce qu'un rôle PEUT faire ; cette échelle dit sur
 * QUI il peut le faire. Sans elle, un MANAGER (qui porte `establishment.team.update`) pouvait
 * s'attribuer le rôle OWNER, rétrograder l'OWNER, lui retirer son accès ou le supprimer.
 */
export const TEAM_MEMBER_ROLE_RANK: Record<TeamMemberRole, number> = {
  [TeamMemberRole.OWNER]: 5,
  [TeamMemberRole.MANAGER]: 4,
  [TeamMemberRole.RECEPTIONIST]: 3,
  [TeamMemberRole.EMPLOYEE]: 2,
  [TeamMemberRole.APPRENTICE]: 1,
  [TeamMemberRole.VIEWER]: 0,
};

/**
 * `actor` peut-il attribuer le rôle `target`, ou agir sur l'accès d'un membre qui a le rôle
 * `target` (changer son rôle, lui retirer l'accès, le supprimer, changer son email de connexion) ?
 * Oui s'il est au moins aussi haut dans l'échelle — donc seul un OWNER agit sur un OWNER.
 */
export function canActOnTeamRole(actor: TeamMemberRole, target: TeamMemberRole): boolean {
  return TEAM_MEMBER_ROLE_RANK[actor] >= TEAM_MEMBER_ROLE_RANK[target];
}
